import React from "react";
import clsx from "clsx";
import * as Icon from "react-feather";

import WComponent from "../common/WComponent";
import BuildProgressService from "../../services/BuildProgressService";
import PlaybookBuildService from "../../services/PlaybookBuildService";
import BuildProgress from "./BuildProgress";
import PlaybookReasonForm from "./PlaybookReasonForm";

import "./build-page.css";
import "./playbook-build-gaps.css";

class PlaybookBuildGaps extends WComponent {
	constructor(props) {
		super(props);
		this.state = {
			waivingGapId: null,
			nameShownFor: {},
			scriptNameFor: {},
		};
	}

	onChooseBuildWithout = ({ gapId }) => {
		this.update({ waivingGapId: gapId });
	};

	onSaveBuildWithout = async ({ reason }) => {
		const { waivingGapId } = this.state;
		const isSaved = await this.props.onWaive({ gapId: waivingGapId, reason });
		if (isSaved) await this.update({ waivingGapId: null });
		return isSaved;
	};

	renderGapChoices({ line }) {
		const { isOpen, isSaving, scriptBusyKey, scriptWriteStartedAt, whyNotWriteAgainFor = {}, keptScriptIdFor = {}, onDrop, onReopen, onWriteScript, onUseScriptAgain, onOpenLibrary } = this.props;
		const { waivingGapId, nameShownFor, scriptNameFor } = this.state;
		if (!isOpen || line.choices.length === 0) return null;
		if (waivingGapId === line.id)
			return (
				<PlaybookReasonForm
					placeholder="Why can this playbook run without it?"
					saveLabel="Build without it"
					isSaving={isSaving}
					onSave={this.onSaveBuildWithout}
					onCancel={() => this.update({ waivingGapId: null })}
				/>
			);
		const isBusy = isSaving || Boolean(scriptBusyKey);
		const isAnotherGapBeingWaived = !!waivingGapId;
		const isLocked = isBusy || isAnotherGapBeingWaived;
		const offersUseScriptAgain = line.choices.includes("useScriptAgain");
		const whyNotWriteAgain = offersUseScriptAgain ? whyNotWriteAgainFor[line.id] || null : null;
		const offersWriteScript = line.choices.includes("writeScript") && !whyNotWriteAgain;
		const isWriting = scriptBusyKey === `writeScript-${line.id}`;
		const isPuttingScriptBack = scriptBusyKey === `useScriptAgain-${line.id}`;
		const isNameOffered = offersWriteScript && !offersUseScriptAgain;
		const isNameShown = isNameOffered && Boolean(nameShownFor[line.id]);
		const typedName = scriptNameFor[line.id] || "";
		const writeClassName = offersUseScriptAgain ? "tk-text-action" : "tk-button tk-button--secondary tk-button--compact";
		const writeLabel = offersUseScriptAgain ? "Ask Workflow to write it again" : "Write a script";
		const chosenScriptKey = isNameShown ? typedName.trim() : null;
		const isWritingProgressShown = isWriting && !!scriptWriteStartedAt;
		const writingGap = { step: line.step, scriptId: keptScriptIdFor[line.id] || null };
		const writingSteps = isWritingProgressShown ? BuildProgressService.scriptWritingSteps({ gap: writingGap }) : [];

		return (
			<div className="playbook-build-gaps__actions">
				{offersUseScriptAgain && (
					<div className="playbook-build-gaps__script-again">
						<button
							type="button"
							className="tk-button tk-button--secondary tk-button--compact"
							onClick={() => onUseScriptAgain({ gapId: line.id })}
							disabled={isLocked}
						>
							<Icon.RotateCcw size={13} />
							{isPuttingScriptBack ? "Putting it back…" : "Use its script again"}
						</button>
						<span className="playbook-build-gaps__note">It comes back as a new revision, which needs approval again.</span>
						{whyNotWriteAgain && <span className="playbook-build-gaps__note">{whyNotWriteAgain}</span>}
					</div>
				)}
				<div className="playbook-build-gaps__choices">
					{offersWriteScript && (
						<button type="button" className={writeClassName} onClick={() => onWriteScript({ gapId: line.id, scriptKey: chosenScriptKey })} disabled={isLocked}>
							{!offersUseScriptAgain && <Icon.Code size={13} />}
							{isWriting ? "Writing…" : writeLabel}
						</button>
					)}
					{isNameOffered && (
						<button
							type="button"
							className="tk-text-action tk-text-action--muted"
							onClick={() => this.update({ nameShownFor: { ...nameShownFor, [line.id]: !isNameShown } })}
							disabled={isLocked}
							aria-expanded={isNameShown}
						>
							{isNameShown ? "Let Workflow name it" : "Choose its name"}
						</button>
					)}
					{line.choices.includes("useFirmScript") && (
						<button type="button" className="tk-text-action" onClick={() => onOpenLibrary({ gapId: line.id })} disabled={isLocked}>
							Use a firm script
						</button>
					)}
					{line.choices.includes("buildWithout") && (
						<button type="button" className="tk-text-action" onClick={() => this.onChooseBuildWithout({ gapId: line.id })} disabled={isLocked}>
							Build without it
						</button>
					)}
					{line.choices.includes("drop") && (
						<button type="button" className="tk-text-action tk-text-action--muted" onClick={() => onDrop({ gapId: line.id })} disabled={isLocked}>
							Drop the step
						</button>
					)}
					{line.choices.includes("removeWaiver") && (
						<button type="button" className="tk-text-action tk-text-action--muted" onClick={() => onReopen({ gapId: line.id })} disabled={isBusy}>
							Remove waiver
						</button>
					)}
					{line.choices.includes("bringBack") && (
						<button type="button" className="tk-text-action tk-text-action--muted" onClick={() => onReopen({ gapId: line.id })} disabled={isBusy}>
							Bring it back
						</button>
					)}
					{line.choices.includes("removeScript") && (
						<button type="button" className="tk-text-action tk-text-action--muted" onClick={() => onReopen({ gapId: line.id })} disabled={isBusy}>
							Take off its script
						</button>
					)}
				</div>
				{isNameShown && (
					<div className="tk-field playbook-build-gaps__name">
						<label className="tk-field-label" htmlFor={`gap-script-name-${line.id}`}>
							Its name
						</label>
						<input
							id={`gap-script-name-${line.id}`}
							className="tk-input"
							value={typedName}
							onChange={event => this.update({ scriptNameFor: { ...scriptNameFor, [line.id]: event.target.value } })}
							disabled={isLocked}
						/>
						<span className="playbook-build-gaps__note">Left empty, Workflow names it.</span>
					</div>
				)}
				{isWritingProgressShown && (
					<BuildProgress steps={writingSteps} slowAfterSeconds={BuildProgressService.SCRIPT_WRITING_SLOW_AFTER_SECONDS} startedAt={scriptWriteStartedAt} />
				)}
			</div>
		);
	}

	render() {
		const { gaps, areScriptsOn, hasFirmScripts, gapIdsWithAKeptScript } = this.props;
		const { openCount, lines } = PlaybookBuildService.describeBuildGaps({ gaps, areScriptsOn, hasFirmScripts, gapIdsWithAKeptScript });
		if (lines.length === 0) return null;
		const hasOpenGaps = openCount > 0;
		const introText = areScriptsOn
			? "The draft can't be built while a step here is open. Write a script for it, build without it with a reason, or drop the step."
			: "The draft can't be built while a step here is open. Build without it, with a reason, or drop the step.";

		return (
			<section className="build-card tk-card">
				<div className="build-card__head">
					<span className="build-card__title">Steps Workflow can't do yet</span>
					{hasOpenGaps ? <span className="tk-badge tk-badge--warning playbook-build-gaps__count">{openCount} open</span> : <span className="tk-muted">All resolved</span>}
				</div>
				{hasOpenGaps && <div className="playbook-build-gaps__intro">{introText}</div>}
				<ul className="playbook-build-gaps__list">
					{lines.map(line => (
						<li key={line.id} className={clsx("playbook-build-gaps__gap", `playbook-build-gaps__gap--${line.status}`)}>
							<span className="playbook-build-gaps__step">
								<span className="playbook-build-gaps__marker" />
								{line.step}
							</span>
							<span className="playbook-build-gaps__detail">{line.detail}</span>
							{this.renderGapChoices({ line })}
						</li>
					))}
				</ul>
			</section>
		);
	}
}

export default PlaybookBuildGaps;
